import React, { useState, useEffect, useImperativeHandle, forwardRef, useRef } from "react";
import { useSelector } from "react-redux";
import { getFetchData } from "../services/supplierService";
import {
  getAllByEntity,
  saveTranslationsBatch,
  deleteTranslation,
  uploadTranslationsExcel,
  downloadTranslationExcelExample,
} from "../services/translationService";
import { toast } from "react-toastify";

/**
 * TranslationTable — qayta ishlatiladigan tarjima komponenti
 *
 * Props:
 *   entityType     — "PROFILE" | "PRODUCT" | ...
 *   entityId       — mavjud ID (tahrirlash rejimida), yangi yozuvda null
 *   fields         — [{ key: "NAME", label: "Nomi" }, ...]
 *   defaultValues  — { NAME: "Kosa", NAME_IN_CATALOG: "Kosa katalog" }
 *                    tarjima bo'lmagan fieldlar uchun default qiymat
 *
 * Ref metodi:
 *   ref.current.save(entityId) — barcha tarjimalarni saqlaydi/o'chiradi
 */
const TranslationTable = forwardRef(({ entityType, entityId, fields, defaultValues = {} }, ref) => {
  const { token } = useSelector((state) => state.user);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // 1. Mamlakatlar ro'yxatini olish
        const countryRes = await getFetchData(
          `${process.env.REACT_APP_HOST_BASE_URL}/v1/country/list`,
          token
        );
        const countries = countryRes?.success ? countryRes.data : [];

        // 2. Har bir mamlakat uchun boshlang'ich row
        const initialRows = countries.map((c) => ({
          countryId: c.id,
          countryName: c.name,
          visible: false,
          // { NAME: { id: null, value: "" }, NAME_IN_CATALOG: { id: null, value: "" }, ... }
          fieldMap: fields.reduce((acc, f) => {
            acc[f.key] = { id: null, value: "" };
            return acc;
          }, {}),
        }));

        // 3. Tahrirlash rejimida mavjud tarjimalarni yuklash
        if (entityId) {
          const translations = await getAllByEntity(token, entityType, entityId);
          translations.forEach((tr) => {
            const row = initialRows.find((r) => r.countryId === tr.countryId);
            if (row && row.fieldMap[tr.field] !== undefined) {
              row.visible = true;
              row.fieldMap[tr.field] = { id: tr.id, value: tr.value || "" };
            }
          });
        }

        setRows(initialRows);
      } catch (err) {
        console.error("TranslationTable load error:", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [entityId, entityType, token]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleVisible = (countryId) => {
    setRows((prev) =>
      prev.map((r) => {
        if (r.countryId !== countryId) return r;
        const turningOn = !r.visible;
        if (!turningOn) return { ...r, visible: false };
        // Yoqilganda — bo'sh fieldlarga defaultValues qo'yish
        const updatedFieldMap = { ...r.fieldMap };
        fields.forEach((f) => {
          if (!updatedFieldMap[f.key].value && defaultValues[f.key]) {
            updatedFieldMap[f.key] = {
              ...updatedFieldMap[f.key],
              value: defaultValues[f.key],
            };
          }
        });
        return { ...r, visible: true, fieldMap: updatedFieldMap };
      })
    );
  };

  const updateField = (countryId, fieldKey, value) => {
    setRows((prev) =>
      prev.map((r) =>
        r.countryId === countryId
          ? {
              ...r,
              fieldMap: {
                ...r.fieldMap,
                [fieldKey]: { ...r.fieldMap[fieldKey], value },
              },
            }
          : r
      )
    );
  };

  // Parent form submit bo'lgandan keyin chaqiriladi
  useImperativeHandle(ref, () => ({
    save: async (resolvedEntityId) => {
      const batchToSave = [];
      const idsToDelete = [];

      rows.forEach((row) => {
        if (row.visible) {
          fields.forEach((f) => {
            const entry = row.fieldMap[f.key];
            if (entry.value.trim()) {
              batchToSave.push({
                id: entry.id || null,
                entityType,
                entityId: resolvedEntityId,
                countryId: row.countryId,
                field: f.key,
                value: entry.value.trim(),
              });
            }
          });
        } else {
          // Unchecked — mavjud tarjimalarni o'chirish
          fields.forEach((f) => {
            if (row.fieldMap[f.key].id) {
              idsToDelete.push(row.fieldMap[f.key].id);
            }
          });
        }
      });

      if (batchToSave.length > 0) {
        await saveTranslationsBatch(token, batchToSave);
      }
      for (const id of idsToDelete) {
        await deleteTranslation(token, id);
      }
    },
  }));

  const handleDownloadExample = async () => {
    try {
      const blob = await downloadTranslationExcelExample(token);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "translation_example.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Shablon yuklab bo'lmadi");
    }
  };

  const handleUploadExcel = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      await uploadTranslationsExcel(token, entityType, entityId, file);
      toast.success("Tarjimalar yuklandi");
      // Reload translations
      if (entityId) {
        const translations = await getAllByEntity(token, entityType, entityId);
        setRows((prev) =>
          prev.map((row) => {
            const updatedFieldMap = { ...row.fieldMap };
            let hasAny = false;
            translations.forEach((tr) => {
              if (tr.countryId === row.countryId && updatedFieldMap[tr.field] !== undefined) {
                updatedFieldMap[tr.field] = { id: tr.id, value: tr.value || "" };
                hasAny = true;
              }
            });
            return { ...row, fieldMap: updatedFieldMap, visible: hasAny ? true : row.visible };
          })
        );
      }
    } catch (err) {
      toast.error("Yuklashda xatolik yuz berdi");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  if (loading) {
    return (
      <div className="col-span-6 mt-4 text-sm text-gray-500">
        Tarjimalar yuklanmoqda...
      </div>
    );
  }

  if (rows.length === 0) return null;

  return (
    <div className="col-span-6 mt-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-700">Tarjimalar</h3>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleDownloadExample}
            className="flex items-center px-3 py-1 text-xs font-medium text-green-700 bg-green-50 border border-green-300 rounded-md hover:bg-green-100"
          >
            Shablon yuklab olish
          </button>
          <label className="flex items-center px-3 py-1 text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-300 rounded-md hover:bg-indigo-100 cursor-pointer">
            {uploading ? "Yuklanmoqda..." : "Excel yuklash"}
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              className="sr-only"
              onChange={handleUploadExcel}
              disabled={uploading}
            />
          </label>
        </div>
      </div>
      <div className="overflow-x-auto border border-gray-200 rounded-md">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-36">
                Davlat
              </th>
              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                Faol
              </th>
              {fields.map((f) => (
                <th
                  key={f.key}
                  className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  {f.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {rows.map((row) => (
              <tr
                key={row.countryId}
                className={row.visible ? "" : "bg-gray-50"}
              >
                <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">
                  {row.countryName}
                </td>
                <td className="px-3 py-2 text-center">
                  <input
                    type="checkbox"
                    checked={row.visible}
                    onChange={() => toggleVisible(row.countryId)}
                    className="w-4 h-4 text-indigo-600 border-gray-300 rounded"
                  />
                </td>
                {fields.map((f) => (
                  <td key={f.key} className="px-3 py-2">
                    <input
                      type="text"
                      disabled={!row.visible}
                      value={row.fieldMap[f.key].value}
                      onChange={(e) =>
                        updateField(row.countryId, f.key, e.target.value)
                      }
                      className="block w-full border-gray-300 rounded-md shadow-sm text-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

export default TranslationTable;
